import React, { useState, useContext, useEffect, useMemo } from "react";
import Dashview from "../Dashview";
import axios from 'axios';
import { v4 } from 'uuid';
import { Prompt, Redirect } from "react-router-dom";
import ApxBusy from 'comps/ApxBusy';
import { AppContext } from "AppContext";
import Dropzone from "react-dropzone";
import { Dots } from "react-activity";
import "react-activity/dist/Dots.css";
import ApxUpload from "../../../comps/ApxUpload";
import ApxEditAddress from "comps/ApxEditAddress";
import Tooltip from 'rc-tooltip';
import Collapse from 'rc-collapse';
import {
  CheckoutProvider,
  PaymentElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout'
import {loadStripe} from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Content = (props) => {

    const [order_uuid, set_order_uuid] = useState("");

    const { identity, config } = useContext(AppContext);

    const [loading, set_loading] = useState(true);
    const [pricing, set_pricing] = useState([]);
    const [materials, set_materials] = useState([]);
    const units = [
        { text: "in", value: "UNITS_IN"},
        { text: "mm", value: "UNITS_MM"},
    ];
    const [upload_dialog_visible, set_upload_dialog_visible] = useState(false);
    const [upload_dialog_percent, set_upload_dialog_percent] = useState(0);
    const [order_errored, set_order_errored] = useState("");
    const [parts, set_parts] = useState([]);
    const [special_pricing, set_special_pricing] = useState(false);
    const [selected_unit, set_selected_unit] = useState("UNITS_IN");
    const [itar, set_itar] = useState(false);
    const [service_charge, set_service_charge] = useState([]);
    const [finalized_weight, set_finalized_weight] = useState(-1);
    const [finalized_subtotal, set_finalized_subtotal] = useState({parts: 0, materials: 0});
    const [account_settings, set_account_settings] = useState({});
    const [addresses, set_addresses] = useState([]);
    const [selected_address, set_selected_address] = useState(0);
    const [shipping_options, set_shipping_options] = useState(null);
    const [checkout_id, set_checkout_id] = useState("");
    const [init_checkout_state, set_init_checkout_state] = useState(null);
    const [redirect, set_redirect] = useState(null);
    
    // Check if any user with impersonation permissions has an identity mismatch
    useEffect(() => {
        const action = async () => {
            try {
                const identity_response = await axios.get(config.api.base + '/ident/verify/' + identity.userAcctId);
                if (identity_response.data.message !== "") set_order_errored(identity_response.data.message);

            } catch(x) {
                console.log(x);
            }
        }
        action(); 
    }, [config.api.base, identity.userAcctId])

    useEffect(() => {
        const action = async () => {
            try {
                const addresses_api_response = await axios.get(config.api.base + '/addresses/account/' + identity.userAcctId);
                const default_address_index = addresses_api_response.data.findIndex((address) => address.default);

                set_addresses(addresses_api_response.data);
                set_selected_address(default_address_index);

                if (addresses_api_response.data.length === 0 || default_address_index === null) {
                    set_order_errored("You have not yet set a shipping address for this account. Please set one up under your Account page.")
                }
            } catch(x) {
                console.log(x);
            }
        }
        action();
    }, [config.api.base, identity.userAcctId, shipping_options])


    useEffect(() => {
        // cleanup checkout session if the user navigates away (does not cover page refreshes)
        return () => {
            if (checkout_id !== "" && init_checkout_state !== null) {
                axios.post(config.api.base + '/checkout/session/' + checkout_id + '/expire');
            }
        }
    }, [config.api.base, checkout_id, init_checkout_state])


    const promise = useMemo(async () => {
        if (shipping_options === null || shipping_options === undefined) return "";

        const res = await axios.post(config.api.base + '/checkout/session', 
            {
                subtotal: finalized_subtotal, order_uuid: order_uuid, 
                shipping_options: shipping_options, account_settings: account_settings
            }
        );  // atm shipping options would fail here
        set_checkout_id(res.data.chunk.id);
        set_init_checkout_state(res.data.chunk);

        set_loading(false);

        return res.data.clientSecret;
    }, [config.api.base, finalized_subtotal, order_uuid, shipping_options]);

    const appearance = {
        theme: 'stripe',
    };

    useEffect(()=>{
        const action = async () => {
            try {
                // Retrieve the pricing and materials tables from the server side of the system:
                // Retrieve material specifications from the database:
                const rxMaterials = await axios.get(config.api.base + '/materials');
                set_materials(rxMaterials.data);

                // Retrieve pricing details from the database:
                let pricing_response = await axios.get(config.api.base + '/pricing');
                
                // If the customer pricing table doesn't contain all materials,
                // retrieve pricing details from default table
                let default_response = await axios.get(config.api.base + '/pricing/tables/27e0793a-2eb6-4418-9f5b-2dc900734a88');
                pricing_response = pricing_response.data.pricing.filter( cust_price => cust_price.offsets.length > 0);
                // filters for materials the customer specific price table does not have
                let default_prices = default_response.data.pricing.filter( d_price => { 
                    return !pricing_response.some( cust_price => cust_price.material === d_price.material ) 
                })
                
                if (identity.userAcctId === '5d28fdb0-cc46-4349-aafd-4403bc8809d6' || identity.userAcctId === '3990addf-d588-4c5b-ad13-92bde1c70e14') {
                    set_special_pricing(true);
                }

                set_pricing(pricing_response.concat(default_prices));

                // Retrieve payment settings for the account
                const account_response = await axios.get(config.api.base + '/admin/accounts/' + identity.userAcctId);
                set_account_settings({
                    card_payment: account_response.data[0].card_payment,
                    tax_exempt: account_response.data[0].tax_exempt,
                    fob: account_response.data[0].fob
                })

                set_loading(false);
            } catch(x) {
                console.log(x);
            }
        }
        action();
    }, [config.api.base, identity.userAcctId])
    

    async function on_process_dropped_files(event) {
        
        const formData = new FormData();
        for (let i = 0; i !== event.length; i++) {
            //const part_identifier = v4();
            //const generatedName = part_identifier + '.' + event.data[i].name.split('.').pop();
            formData.append("file-" + i, event[i]);
        }

        const response = await axios.post(config.api.base + '/parts', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: on_upload_progress
        })

        set_parts(parts.concat(response.data));
        set_upload_dialog_visible(false);
        set_upload_dialog_percent(0);
    }

    function on_upload_progress(event) {
        const percentage = 100 * (event.loaded / event.total);
        set_upload_dialog_percent(percentage);
        
    }

    function on_files_dropped(files) {
        set_upload_dialog_percent(0);
        set_upload_dialog_visible(true);
        on_process_dropped_files(files);
    }

    function on_part_changed(args) {
        const newParts = parts.map((part) =>  part.id === args.id ? args.data : part );
        set_parts(newParts);
    }

    function calc_shipping_weight() {
        const total_weight_g = parts.reduce((sum, current_part) => {
            let volume_cm = selected_unit === "UNITS_IN" ? current_part.volume * 16387.064 : current_part.volume;
            let material_density = materials.find((material) => material.name === current_part.material).density;
            return (volume_cm * material_density) + sum;
        }, 0.);
        const total_weight_lb = total_weight_g / 453.59237;

        // VERY ROUGH estimate size of shipping box
        const total_volume_in = parts.reduce((sum, current_part) => 
            selected_unit === "UNITS_IN?" ? sum + current_part.volume : sum + current_part.volume / 16387.064, 
        0.);

        const box_weights_by_vol = { //volume in^3 and weight lbs
            "512": ".5",      //8x8x8
            "1190": ".85",    //17.25x11.5x6 (rounded)
            "1728": "1",      //12x12x12
            "4096": "1.65",   //16x16x16
            "5832": "2.1",    //18x18x18
            "8000": "2.5",    //20x20x20
        }

        const box_volumes = Object.keys(box_weights_by_vol).map(key => parseInt(key));
        const selected_box_volume = Math.min.apply(Math, box_volumes.filter(vol => vol > total_volume_in)).toString();

        const box_weight = isFinite(selected_box_volume) ? +box_weights_by_vol[selected_box_volume] : 4.;

        // Packers typically round up shipping weight to the nearest lb 
        return Math.ceil(total_weight_lb + box_weight);
    }

    function generate_shipping_options(package_weight) {
        const action = async () => {
            try {
                const shipment_request = {
                    name: addresses[selected_address].name,
                    address_1: addresses[selected_address].address_1,
                    address_2: addresses[selected_address].address_2,
                    city: addresses[selected_address].city,
                    state: addresses[selected_address].state,
                    zip: addresses[selected_address].zip,
                    country: addresses[selected_address].country,
                    weight: package_weight
                }
                const shipping_options = await axios.post(config.api.base + '/checkout/shipping', shipment_request); // atm shipping would fail here 

                set_shipping_options(shipping_options.data.shipping_options);
                
            } catch (e) {
                console.log(e); //atm console would be here
            }
        }
        action();
    }


    async function on_clicked_to_payment(args) {
        set_order_errored("");
        set_loading(true);
        
        try {
            const surcharges = await axios.post(config.api.base + '/surcharges/assess', {parts: parts});
            
            set_service_charge(surcharges.data);

            set_order_uuid(crypto.randomUUID());
        
            set_finalized_subtotal({
                parts: Math.round(order_total.toFixed(2) * 100), 
                materials: Math.round(surcharges.data.reduce(
                    (acc, surcharge) => acc + (surcharge.unit_price * surcharge.order_qty),
                    0.
                ) * 100)
            });

            const finalized_weight = calc_shipping_weight();
            set_finalized_weight(finalized_weight);
            generate_shipping_options(finalized_weight);
        } catch (e) {
            console.log(e);
            set_order_errored("We encountered technical difficulties. Please refresh the page and try again.");
            set_loading(false);
            return;
        }

    }

    function on_flip_special_pricing(args) {
        set_special_pricing(args.target.checked);
    }

    function on_change_selected_unit(args) {
        set_selected_unit(args.target.value);
    }
    
    function on_change_itar(args) {
        set_itar(args.target.value === "true");
    }

    function onDeletePart(index) {
        set_parts(parts.toSpliced(index, 1));
    }

    function reset_checkout_session() {
        set_shipping_options(null);
        set_init_checkout_state(null);
    }

    // If a redirect target is specified, redirect to it:
    if(redirect != null) {
        return <Redirect to={{
            pathname: redirect
        }}/>
    }

    const num_parts = parts.reduce((sum, current) => current.quantity ? +current.quantity + sum : sum, 0);
    const order_total =  parts.reduce((sum, current) => current.price_total ? current.price_total + sum : sum, 0);
    const to_payment_button_disabled = parts.length === 0 || parts.some((part) => !part.snapshot_filename) || 
        addresses.length === 0 || selected_address === null;

    if (loading) {
        return(
            <div className={"container mx-auto"}>
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Additive Printing - New Order{identity.userAdmin && ` - ${identity.userComp}`}
                    </h2>
                    <p className="mt-2 text-center text-xl text-gray-600">
                        You can upload 3MF/STL files here to create an order.
                    </p>
                </div>
                <div className={"w-full mt-4"}>
                    <LoadScreen></LoadScreen>
                </div>
            </div>
        );
    }

    if (account_settings.card_payment === null || account_settings.tax_exempt === null || account_settings.fob === null) {
        return(
            <div className={"container mx-auto"}>
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Additive Printing - New Order{identity.userAdmin && ` - ${identity.userComp}`}
                    </h2>
                    <p className="mt-2 text-center text-xl text-gray-600">
                        You can upload 3MF/STL files here to create an order.
                    </p>
                </div>
                <div className={"w-full mt-4 mb-4 space-y-2"}>
                    <div className="errorBox">Your account setup has not been finalized. Please contact jobtravelers@abcorp.com.</div>
                </div>
            </div>
        );
    }

    if (init_checkout_state === null) {
    return(
        <div className={"container mx-auto"}>
            <div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    Additive Printing - New Order{identity.userAdmin && ` - ${identity.userComp}`}
                </h2>
                <p className="mt-2 text-center text-xl text-gray-600">
                    You can upload 3MF/STL files here to create an order.
                </p>
            </div>
            <div className={"w-full mt-4 mb-4 space-y-2"}>
                <div className={"flex w-full space-x-4"}>
                    <div className={"items-left"}>
                        <label htmlFor="units" className="block text-sm font-medium text-gray-700">
                            Display Units
                        </label>
                        <select id="units" name="units" value={selected_unit} onChange={on_change_selected_unit} disabled={ parts.some((part) => !part.snapshot_filename) } className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                            { units.map((item, index) => {
                                return (
                                    <option value={item.value} key={index}>{item.text}</option>
                                )
                            })}
                        </select>
                    </div>
                    <div className={"items-left"}>
                        <label htmlFor="itar" className="block text-sm font-medium text-gray-700">
                            ITAR Parts
                        </label>
                        <select id="itar" name="itar" value={itar} onChange={on_change_itar} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                        </select>
                    </div>
                    {
                        identity.userAdmin && 
                        <div className={"flex justify-end flex-grow items-end"}>
                            <div className={"mb-1 mr-2 text-gray-700"}>
                                <label htmlFor="pricingToggle">
                                    Pricing by: 
                                </label>
                            </div>
                            <div className={ "mr-2 mb-1 font-medium " + (special_pricing ? "text-gray-300" : "text-green-350") }>
                                Volume
                            </div>
                            <label className={"switch"}>
                                <input id="pricingToggle" type="checkbox" onChange={on_flip_special_pricing} checked={special_pricing}/>
                                <span className={"slider round"}></span>
                            </label>
                            <div className={ "ml-2 mb-1 font-medium " + (special_pricing ? "text-indigo-500" : "text-gray-300") }>
                                Part
                            </div>
                        </div>
                    }
                </div>
            </div>
            <div className={parts.length > 0 ? "w-full border-t border-b border-gray-400 divide-y divide-gray-400 rounded-md mt-6" : "w-full"}>
                {
                    parts.map((item, index) => {
                        return(
                            <Part 
                                key={item.id} 
                                part={item} 
                                onDelete={()=>{onDeletePart(index)}} 
                                special_pricing={special_pricing}
                                display_unit={selected_unit}
                                on_changed={(e)=>on_part_changed(e)} 
                                pricing={pricing} 
                                materials={materials}>
                            </Part>
                        );
                    })
                }
            </div>
            <div className={"w-full border border-dashed border-gray-600 rounded-sm bg-indigo-50 h-16 my-4 px-4"}>
                <Dropzone  onDrop={on_files_dropped} accept=".stl,.3mf">
                    {({getRootProps, getInputProps}) => (
                        <section style={{ height: "100%"}}>
                            <div style={{ height: "100%"}} className={"flex items-center justify-center"} {...getRootProps()}>
                                <input {...getInputProps()} />
                                <p>Drag and drop 3MF/STL files here, or click to select files</p>
                            </div>
                        </section>
                    )}
                </Dropzone>
            </div>
            { order_errored !== "" &&
                <div className={"mb-1 errorBox"}>
                    <p>{order_errored}<br/>
                    If errors persist, please reach out to&nbsp; 
                    <a className="underline text-red-900" href={"mailto:jobtravelers@abcorp.com?subject=Trouble%20Placing%20Order"}>
                        jobtravelers@abcorp.com
                    </a>.</p>
                </div>
            }
            <div className={"flex py-2 justify-between"}>
                <div className={"text-2xl text-gray-900"}>
                    Order subtotal: 
                    <span className={"font-bold"}>
                        &nbsp;${ order_total.toFixed(2) }&nbsp;
                    </span>
                    for 
                    { num_parts === 1 ? " 1 item" : " " + num_parts + " items" } 
                </div>
                <div className={"flex flex-row"}>
                    <button disabled={ to_payment_button_disabled } className={"estimateButton"} onClick={on_clicked_to_payment} style={{ marginRight: '5px'}}>
                        Proceed to Payment
                    </button>
                </div>
            </div>
            { itar &&
                <div className={"mb-1 errorBox"}>
                    <p><span className="font-bold">ITAR Compliance:</span> Items or data in this order may be subject to <span className="font-bold">U.S. ITAR/EAR export controls</span>. 
                    They may not be shared or exported to non-U.S. persons without prior U.S. Government authorization. Unauthorized transfer is prohibited by law.</p>
                </div>
            }
            { parts.length > 3 &&
                <div className={"flex pt-4 pb-1 justify-center"}>
                    <button onClick={ () =>  window.scroll({top: 0, behavior: 'smooth'}) } className={"cursor-pointer text-sm text-indigo-500"}>
                        Jump to Top
                    </button>
                </div>
            }
            <ApxBusy message={"Uploading Files..."} show={upload_dialog_visible} show_progress={true} percentage={upload_dialog_percent}/>
            <Prompt when={true} message="Any data you have entered on this page will be lost, are you sure you want to leave this page?" />
        </div>
    );
    }    

    return(
        <div className={"container mx-auto space-y-4"}>
            <div className="w-full">
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    Additive Printing - Payment{identity.userAdmin && ` - ${identity.userComp}`}
                </h2>
                <p className="mt-2 text-center text-xl text-gray-600">
                    Fill out all shipping and payment information to complete your order.
                </p>
            </div>
            <CheckoutProvider
                stripe={stripePromise}
                options={{
                    fetchClientSecret: () => promise,
                    elementsOptions: {appearance},
                }}
            >
                <PaymentPage 
                    order_uuid={order_uuid}
                    checkout_id={checkout_id}
                    init_checkout_state={init_checkout_state}
                    parts={parts}
                    itar={itar}
                    addresses={addresses}
                    selected_address={selected_address}
                    service_charge={service_charge}
                    finalized_weight={finalized_weight}
                    finalized_subtotal={finalized_subtotal}
                    account_settings={account_settings}
                    set_redirect={set_redirect}
                    go_back={reset_checkout_session}>
                </PaymentPage>
            </CheckoutProvider>
            <Prompt when={true} message="Any data you have entered on this page will be lost, are you sure you want to leave this page?" />
        </div>
    );
}

const PaymentPage = (props) => {
    const is_card_payment = props.account_settings.card_payment;
    const is_tax_exempt = props.account_settings.tax_exempt;
    const is_fob_3rd_party = props.account_settings.fob;

    const [addresses, set_addresses] = useState(props.addresses);
    const [selected_address, set_selected_address] = useState(props.selected_address);

    const [show_addr_modal, set_show_addr_modal] = useState(false);
    const [upload_dialog_visible, set_upload_dialog_visible] = useState(false);
    const [upload_dialog_percent, set_upload_dialog_percent] = useState(0);
    const [upload_invoice_dialog_visible, set_upload_invoice_dialog_visible] = useState(false);
    const [purchase_order, set_purchase_order] = useState(is_card_payment ? "cc-stripe" : "");
    const [original_invoice, set_original_invoice] = useState("");
    const [internal_invoice, set_internal_invoice] = useState("");
    const [stripe_loading, set_stripe_loading] = useState(true);
    const [email, set_email] = useState('');
    const [email_error, set_email_error] = useState(null);
    const [app_busy, set_app_busy] = useState("");
    const [order_errored, set_order_errored] = useState("");
    

    const { identity, config } = useContext(AppContext);


    const checkoutState = useCheckout();  // use only for functions. data fields are unreactive in ui_mode custom
   
    const {checkout} = checkoutState;

    const [checkout_items, set_checkout_items] = useState(null);
    const [checkout_total_details, set_checkout_total_details] = useState(props.init_checkout_state.total_details);
    const [checkout_amount_total, set_checkout_amount_total] = useState(props.init_checkout_state.amount_total);
    const [checkout_shipping, set_checkout_shipping] = useState(props.init_checkout_state.shipping_options); 
    const [selected_shipping, set_selected_shipping] = useState("");
    const [show_cart, set_show_cart] = useState(true);
    const [pay_later, set_pay_later] = useState(false);


    useEffect(() => { 
        if (checkoutState.type === 'success') {
            set_checkout_items(checkoutState.checkout.lineItems);
            change_shipping_options(addresses[selected_address])
        }
    // eslint-disable-next-line
    }, [checkoutState.type, selected_address]);


     if (checkoutState.type === 'error') {
        console.log(checkoutState.error.message)
        return (
        <div>Error: {checkoutState.error.message}</div>
        );
    } else if (checkoutState.type === 'loading' || checkout_items == null) {
        return(
            <div className={"w-full mt-4"}>
                <LoadScreen></LoadScreen>
            </div>
        );
    }


    function handle_shipping_change(shipping_option_id) {
        return async () => {
            try {
                set_selected_shipping("");
                set_app_busy("Processing...");

                await checkoutState.checkout.updateShippingOption(shipping_option_id);
                const updated_session = await axios.get(config.api.base + '/checkout/session/' + props.checkout_id);

                set_checkout_amount_total(updated_session.data.chunk.amount_total);
                set_checkout_total_details(updated_session.data.chunk.total_details);
                set_selected_shipping(shipping_option_id);
                set_app_busy("");
            } catch (e) {
                console.log(e)
            }
        }
    }

    function onAddNewAddr(_) {
        set_show_addr_modal(true);
    }

    function onCancelNewAddr() {
        set_show_addr_modal(false);
    }
    
    function onSaveNewAddr(new_addr) {
        const action = async () => {
            try {
                new_addr.acctId = identity.userAcctId;

                if (new_addr.default) { 
                    const old_default_addr_index = addresses.findIndex((addr) => addr.default);
                    if (old_default_addr_index !== -1) {
                        const old_default_addr = { ...addresses[old_default_addr_index], default: false};
                        await axios.put(config.api.base + '/addresses/' + old_default_addr.id, old_default_addr);
                    }
                }

                await axios.post(config.api.base + '/addresses', new_addr);

                const action = async () => {
                    const address_api_response = await axios.get(config.api.base + '/addresses/account/' + identity.userAcctId);
                    set_addresses(address_api_response.data);
                    set_selected_address(address_api_response.data.length - 1);
                    

                    set_checkout_shipping([]);
                }
                action();

            } catch (e) {}
        }
        action();
        set_show_addr_modal(false);
    }

    function change_shipping_options(new_addr) {
        const action = async () => {
            try {

                if (is_fob_3rd_party) {
                    const dummy_address = {
                        shipping_address: {
                            country: 'US',
                            line1: "225 Rivermoor St",
                            line2: "",
                            city:  "Boston",
                            state: "MA",
                            postal_code: "02132",
                            country: "US"
                        },
                        shipping_options: [
                            {
                                shipping_rate_data: {
                                    display_name: 'Third Party',
                                    fixed_amount: {
                                        amount: 0,
                                        currency: "usd"
                                    },
                                    tax_behavior: "exclusive",
                                    tax_code: "txcd_92010001",
                                    type: "fixed_amount"
                                }
                            }
                        ]
                    }

                    const stripe_update = await axios.post(config.api.base + '/checkout/session/' + props.checkout_id, 
                        dummy_address
                    );


                    set_selected_shipping("");
                    set_checkout_shipping(stripe_update.data.chunk.shipping_options);
                    set_checkout_amount_total(stripe_update.data.chunk.amount_total);
                    set_checkout_total_details(stripe_update.data.chunk.total_details);

                    const set_dummy_shipping = handle_shipping_change(stripe_update.data.chunk.shipping_options[0].shipping_rate.id);
                    set_dummy_shipping();
                    return;
                }

                // retrieve updated shipping quotes
                // format for ups
                const shipment_request = {
                    name: new_addr.name,
                    address_1: new_addr.address_1,
                    address_2: new_addr.address_2,
                    city: new_addr.city,
                    state: new_addr.state,
                    zip: new_addr.zip,
                    country: new_addr.country,
                    weight: props.finalized_weight
                }
                const shipping_options = await axios.post(config.api.base + '/checkout/shipping', shipment_request);

                // format for stripe
                const shipping_address = {
                    shipping_address: {
                        line1: new_addr.address_1,
                        line2: new_addr.address_2,
                        city:  new_addr.city,
                        state: new_addr.state,
                        postal_code: new_addr.zip,
                        country: new_addr.country
                    }
                }
                
                // update stripe's list of available shipping options
                const stripe_update = await axios.post(config.api.base + '/checkout/session/' + props.checkout_id, {
                    ...shipping_address,
                    ...shipping_options.data
                });

                set_selected_shipping("");
                set_checkout_shipping(stripe_update.data.chunk.shipping_options);
                set_checkout_amount_total(stripe_update.data.chunk.amount_total);
                set_checkout_total_details(stripe_update.data.chunk.total_details);

            } catch (e) {
                console.log(e);
            }
        }
        action();
    }

    function onSaveEditAddr(_) {    }

    function onChangeAddrTable(args) {
        set_selected_address(args.target.value);
        set_checkout_shipping([]);
        set_selected_shipping("");
    }

    function on_assign_invoice_number(args) {
        if (args.target.value.trim() !== "") {
            set_purchase_order(args.target.value);
        }
    }

    function on_upload_custom_invoice(args) {
        set_upload_invoice_dialog_visible(true);
    }

    function on_clicked_upload_invoice_okay(args) {
        on_process_upload_invoice(args);
        set_upload_invoice_dialog_visible(false);
        set_upload_dialog_visible(true);
    }

    function on_clicked_upload_invoice_cancel(args) {
        set_upload_invoice_dialog_visible(false);
    }

    async function on_process_upload_invoice(event) {
        console.log("::::: UPLOAD CUSTOM INVOICE :::::");
        console.log(event);

        try {
            const formData = new FormData();
            const generatedName = v4() + '.' +  event[0].name.split('.').pop();
            formData.append("file-1" , event[0], generatedName);
    
            await axios.post(config.api.base + '/temp', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: on_upload_progress
            })
    
            // We now have the original filename and the generated name
            // that is used to identify the file on the server. We need
            // to add both of those fields to the order record:
            set_original_invoice(event[0].name);
            set_internal_invoice(generatedName);

        } catch (x) {
            console.log(x);
        } finally {
            set_upload_dialog_visible(false);
            set_upload_dialog_percent(0);
        }

    }

    function on_upload_progress(event) {
        const percentage = 100 * (event.loaded / event.total);
        set_upload_dialog_percent(percentage);
        
    }

    async function handle_email_blur() {
        if (!email) {
            return;
        }
        const { email_is_valid, email_error_message } = await validate_email(email, checkout);
        if (!email_is_valid) {
            set_email_error(email_error_message);
        }
    };

    function handle_email_change(e) {
        set_email_error(null);
        set_email(e.target.value);
    };

    const collapsed_shipping_addr = {
      label: 'Shipping Address - ' + (addresses[selected_address]?.address_1 ),
      children: 
        <div className="flex flex-col p-4">
            <div className="flex">
                {
                    addresses.length ?
                    <div className={"items-left w-1/2"}>
                        <label htmlFor="shipAddress" className={"block text-sm font-medium text-gray-700"}>
                            Shipping Address for All Lines
                        </label>
                        <select
                            id="shipAddress"
                            value={selected_address}
                            onChange={onChangeAddrTable}
                            size='1' 
                            className="mt-1 block w-full py-2 px-3 overflow-ellipsis border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                            {addresses.map((item, index) => {
                                const state_zip = item.state + " " + item.zip;
                                let address_string = [item.name, item.org, item.address_1, item.address_2, item.city, state_zip, item.country].filter(Boolean).join(", ");
                                return <option key={index} value={index}>{address_string}</option>
                            })}
                        </select>
                    </div>
                    :
                    <div className={"content-end w-1/2 pb-2 text-medium text-gray-700"}>
                        Please add a Shipping Address. <span className="text-red-600"> *</span>
                    </div>
                }
                <div className={"content-end pl-4"}>
                    <button className={"standardButton h-10"} onClick={onAddNewAddr} style={{ marginRight: '5px'}}>
                        Add Address
                    </button>
                </div>
                <div className="content-center pl-3">
                    <Tooltip 
                        placement="top"
                        mouseLeaveDelay="0.1"
                        overlay={
                            <p>Addresses can always be edited or deleted from your Account page.</p>
                        }
                        overlayInnerStyle={{background: 'white', color: 'black', fontSize: '1rem', lineHeight: '1.5rem', whiteSpace: 'pre-line'}}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className={addresses.length ? "h-6 mt-4 pr-3" : "h-6 mt-4 pr-3 mb-4"} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" strokeWidth={1.5} d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                        </svg>
                    </Tooltip>
                </div>
            </div>
        </div>,
    };

    const collapsed_shipping_method = {
        label: 'Shipping Method',
        children: selected_address > -1 ?
        <div className="p-4">
             { !is_fob_3rd_party &&
                <div> 
                    <div className={"text-sm font-medium mb-2 text-gray-700"}>
                        Select Shipping Method
                    </div>
                    { checkout_shipping.length === 0 && <div className="flex justify-center"><Dots size={32} /></div> }
                    <div className="flex flex-col space-y-2">
                        {checkout_shipping.map((option) => {
                            return (
                            <label className="flex items-center" key={option.shipping_rate.id}>
                                <input
                                className="mr-2"
                                type="radio"
                                name="shippingOption"
                                value={option.shipping_rate.id}
                                checked={selected_shipping === option.shipping_rate.id}
                                onChange={handle_shipping_change(option.shipping_rate.id)}
                                />
                                {option.shipping_rate.display_name} - ${(option.shipping_rate.fixed_amount.amount / 100).toFixed(2)}
                            </label>
                            );
                        })}
                    </div>
                    { checkout_shipping.length !== 0 &&
                        <div className="mt-2 italic text-gray-500 text-sm leading-snug">
                            *Shipping cost estimates are based on current carrier rates and are subject to change.<br/>
                            Final freight charges may vary slightly based on market rates at the time of shipment.
                        </div>
                    }
                </div>
            }

        </div> 
        :
        <div>Please select a Ship To Address to view your shipping options.</div>
        
    };

    const collapsed_po = {
      label: 'Purchase Order - ' + purchase_order,
      children: 
        <div className="flex flex-col p-4 space-y-2 2xl:flex-row 2xl:space-x-2 2xl:space-y-0">
            <div className={"items-left"}>
                <label htmlFor="invoiceNumber" className={"block text-sm font-medium text-gray-700"}>
                    Customer Purchase Order<span className="text-red-600"> *</span>
                </label>
                <input id={"invoiceNumber"} type={"text"} value={purchase_order} onChange={on_assign_invoice_number} size='25' className={"mt-1 block py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"}/>
            </div>
            <div className={"flex"}>
                <div>
                    <label htmlFor="invoiceFile" className={"block text-sm font-medium text-gray-700"}>
                        Purchase Order Filename
                    </label>
                    <input id={"invoiceFile"} type={"text"} value={original_invoice} size='25' readOnly={true} className={"mt-1 block py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none text-sm"}/>
                </div>
                <div className={"flex content-end items-end ml-2"}>
                    <button className={"standardButton h-10"}  onClick={on_upload_custom_invoice}>
                        <div className={"flex"}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            <div>Attach P.O.</div>
                        </div>
                    </button>
                </div>
            </div>
        </div>,
    };

    const collapsed_payment = { 
      label: 'Payment',
      children: 
        <div className="px-4 pt-4 pb-6">
            { stripe_loading && <div className="flex justify-center"><Dots size={32} /></div> }
            <PaymentElement id="payment-element" options={{layout: {type: "tabs" }}} onReady={() => set_stripe_loading(false)}/>
            { !stripe_loading &&
                <div className="flex flex-col mt-4">
                    <label for="stripe-email" className="text-gray-800">
                        Payment Email
                    </label>
                    <input
                    id="stripe-email"
                    type="text"
                    value={email}
                    onChange={handle_email_change}
                    onBlur={handle_email_blur}
                    className={"block py-2 px-3 bg-white text-gray-800 text-base rounded-md shadow-sm focus:outline-none " + (email_error ? "border-2 border-red-500" : "border border-gray-200") /** TODO should email_error be "" instead of null, check logic */}
                    />
                { email_error && <div id="email-errors" className="text-red-500">{email_error}</div> }
                </div>
            }
        </div>
    };

    const collapse_items = [
        collapsed_shipping_addr,
        ...(is_fob_3rd_party ? [] : [collapsed_shipping_method]),
        collapsed_po,
        ...(is_card_payment ? [collapsed_payment] : [])
    ];

    function on_clicked_place_order(args) {
        set_app_busy("Placing Order...");
        set_order_errored("");
        on_process_place_order();
    }

    async function on_process_place_order() {
        const pay_now = is_card_payment && !pay_later;
        if (pay_now) {
            const { email_is_valid, email_error_message } = await validate_email(email, checkout);

            if (!email_is_valid) {
                set_email_error(email_error_message);
                set_order_errored(email_error_message);
                set_app_busy("");
                return;
            }
        }

        if (addresses[selected_address].acctId !== identity.userAcctId) {
            set_order_errored("Identity mismatch detected. Try refreshing the page or logging out and in again.");
            return;
        }


        try {
            const record = {
                uuid: props.order_uuid,
                address: addresses[selected_address], 
                shipping_method: !is_fob_3rd_party ? 
                    checkout_shipping.find(option => option.shipping_rate.id === selected_shipping).shipping_rate.display_name :
                    "",
                amount_shipping: !is_fob_3rd_party ? checkout_total_details.amount_shipping / 100 : 0., 
                itar: props.itar,
                metadata: { 
                    parts: [...props.parts], 
                    amount_tax: (is_card_payment && !is_tax_exempt) ? 
                    checkout_total_details.amount_tax / 100 : 0.
                },
                surcharges: props.service_charge, 
                purchase_order: purchase_order,
                original_invoice: original_invoice,
                internal_invoice : internal_invoice,
                card_payment: pay_now
            }
            console.log("::::: PLACED ORDER :::::")
            console.log(record);
            await axios.post(config.api.base + "/orders", record).catch((error) => { 
                if (error.response) console.log("ERROR: " + error.response.data);
                throw error;
            });

            if (pay_now) { 
                const checkout_result = await checkout.confirm();

                // This point will only be reached if there is an immediate error when
                // confirming the payment. Otherwise, your customer will be redirected to
                // your `return_url`. For some payment methods like iDEAL, your customer will
                // be redirected to an intermediate site first to authorize the payment, then
                // redirected to the `return_url`.
                if (checkout_result.type === 'error') {
                    set_order_errored(checkout_result.error.message);
                    set_app_busy("");
                }
                console.log(checkout_result);
            } else {
                props.set_redirect("/dashboard/orders/submitted/" + props.order_uuid);
            }
        } catch(err) {
            console.log(err);
            set_order_errored("We encountered an error while attempting to place this order. Please wait a moment before trying again.");
            set_app_busy("");
            return;
        }
    }

    function toggleCart() {
        set_show_cart((previous_state) => !previous_state);
    }

    function on_clicked_pay_later(args) {
        set_pay_later(args.target.checked);
    }


    const order_button_disabled = addresses.length === 0 || 
        purchase_order === "" ||
        selected_shipping === "";

    const num_parts = props.parts.reduce(
        (sum, current) => current.quantity ? +current.quantity + sum : sum, 0);
    const pretty_parts_cost = checkout_items?.reduce(
        (acc, item) => acc + (item.name === "3D Products"? item.subtotal.amount : ""), ""); //magic
    const pretty_surcharges = checkout_items?.reduce(
        (acc, item) => acc + (item.name !== "3D Products"? item.subtotal.amount : ""), ""); //magic
        
    const pretty_shipping_cost = "$" + (
        !is_fob_3rd_party ?
        (checkout_total_details.amount_shipping / 100).toFixed(2) :
        "0.00"
    );
    const pretty_tax = "$" + (
        is_card_payment && !is_tax_exempt ?
        (checkout_total_details.amount_tax / 100).toFixed(2) :
        "0.00"
    );

    const is_taxable = !checkout_total_details.breakdown.taxes.some((item) => item.taxability_reason === "not_collecting");
    const tax_rate = is_taxable ?
        (checkout_total_details.breakdown.taxes.find((item) => item.taxability_reason === "standard_rated")?.rate.effective_percentage ?? "-.--") + "%" :
        "0.00%";

    const pretty_total = "$" + (checkout_amount_total / 100).toFixed(2); 

    if (addresses === null) return null; 

    return(
        <div className={"w-full mt-4 mb-4 flex flex-wrap"}>
            <div className="w-full">
                <button className="underline" onClick={() => props.go_back()}>
                    ← Return to Cart
                </button>
            </div>
            <div className="w-1/2 py-4 pr-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Information</h3>
                <Collapse 
                    defaultActiveKey={0}
                    accordion={true}
                    items={collapse_items} 
                    forceRender={true}
                />
            </div>
            <div className="w-1/2 flex flex-col py-4 pl-4 pr-4 lg:pr-8 xl:pr-20 2xl:pr-32">
                <div className="flex flex-row justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">Order Summary</h3>
                    <button className="underline" onClick={toggleCart}>{ show_cart ? "Hide Cart" : "Show Cart"}</button>
                </div>
                { props.itar &&
                    <div className={"mb-4 errorBox"}>
                        <p><span className="font-bold">ITAR Compliance:</span> Items or data in this order may be subject to <span className="font-bold">U.S. ITAR/EAR export controls</span>. 
                        They may not be shared or exported to non-U.S. persons without prior U.S. Government authorization. Unauthorized transfer is prohibited by law.</p>
                    </div>
                }
                { show_cart &&
                <div className="mb-3">
                    {
                        props.parts.map((part, index) => {
                            return(
                                <div className="flex flex-col mb-1">
                                    <div className="flex justify-between">
                                        <p className="font-medium text-lg line-clamp-2">{part.original_filename}</p>
                                        <p className="self-end font-medium text-lg ml-2">{"$" + part.price_total.toFixed(2)}</p>
                                    </div>
                                    <div className="flex justify-between">
                                        <p className="text-base font-light italic">{part.material} : {readable_finishes.find(finish => finish.value === part.finish).text}</p>
                                        <p className="self-end font-light text-base italic ml-2">${part.price_per_component.toFixed(2)} x {part.quantity}</p>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
                }
                { show_cart && 
                    <hr className="w-full mb-4"/>
                }
                <div className="flex flex-col mb-4">
                    <div className="flex justify-between">
                        <p className="text-lg">Order Subtotal</p>
                        <p className="font-semibold text-lg">{pretty_parts_cost}</p>
                    </div>                    
                    { pretty_surcharges !== "" &&
                        <div className="flex justify-between">
                            <p className="text-lg">Service Fee</p>
                            <p className="font-semibold text-lg">{pretty_surcharges}</p>
                        </div>    
                    }
                    { !is_fob_3rd_party &&
                        <div className="flex justify-between">
                            <p className="text-lg">Shipping</p>
                            <p className="font-semibold text-lg">{selected_shipping === "" ? "--" : pretty_shipping_cost}</p>
                        </div>
                    }
                    { is_card_payment && !is_tax_exempt &&
                        <div className="">
                            <div className="flex justify-between">
                                <p className="text-lg">Taxes <span className="text-base font-light italic">({tax_rate})</span></p>
                                <p className="font-semibold text-lg">{(selected_shipping === "" && is_taxable) ? "--" : pretty_tax}</p>
                            </div>
                            { !is_taxable &&
                                <p className="text-base font-light italic">
                                    *Tax not applicable for selected state
                                </p>
                            }
                        </div>
                    }
                </div>
                <hr className="w-full"/>
                <div className="flex justify-between my-4">
                    <p className="font-semibold text-2xl">Order Total for { num_parts === 1 ? "1 item" : " " + num_parts + " items" }</p>
                    <p className="font-bold text-2xl">
                        {selected_shipping === "" ? "--" : pretty_total}
                    </p>
                </div>
                <div className="w-full text-right">
                    <button disabled={ order_button_disabled } className={"estimateButton"} onClick={on_clicked_place_order}>
                        Place Order
                    </button>
                </div>
                { identity.userAdmin && is_card_payment &&
                    <div className="w-full text-right">
                        <input id="admin-override" name="admin-override" type="checkbox" checked={pay_later} onChange={on_clicked_pay_later}/>
                        <label htmlFor="admin-override" className="pl-2">Check to allow customer to pay for this order later</label>
                    </div>
                }
                { order_errored !== "" && 
                    <div className={"errorBox mt-2"}>
                        <p>{order_errored}<br/>
                        If you need help placing your order, please reach out to&nbsp; 
                        <a className="underline text-red-900" href={"mailto:jobtravelers@abcorp.com?subject=Trouble%20Placing%20Order"}>
                            jobtravelers@abcorp.com
                        </a>.</p>
                    </div>
                }
            </div>
            <ApxBusy message={"Uploading Files..."} show={upload_dialog_visible} show_progress={true} percentage={upload_dialog_percent}/>
            <ApxBusy message={app_busy} show={app_busy !== ""} show_progress={false} percentage={0}/>
            <ApxUpload show={upload_invoice_dialog_visible} onClickedOkay={on_clicked_upload_invoice_okay} onClickedCancel={on_clicked_upload_invoice_cancel}/>
            <ApxEditAddress show={show_addr_modal} data={null} onNew={onSaveNewAddr} onEdit={onSaveEditAddr} onCancel={onCancelNewAddr}></ApxEditAddress>
        </div>
    );
}

const validate_email = async (email, checkout) => {
  const update_result = await checkout.updateEmail(email);
  const email_is_valid = update_result.type !== "error";

  return { email_is_valid, email_error_message: email_is_valid ? null : update_result.error.message };
}

const LoadScreen = () => {
    return(
        <div>
            <div className={"flex flex-row justify-center items-center"}>
                <img src="/loading-transparent.gif" className="w-1/4 xl:w-1/5" 
                    alt="A loading gif - Many 2D planes moving and intersecting in 3D" />
                <div className={"flex-col text-center pb-8 pr-32"}>
                    <p className={"text-2xl lg:text-3xl"}>Loading...</p>
                    <Dots size={32}/>
                </div>
            </div>
        </div>
    );
}

// material g-uids maintained here for backwards compatibility
const finishes_by_material = [
    {   // HP Polypro
        material: ["0d96b14d-99d1-42cb-a767-3f10a8d61969", "HP Polypro"], 
        finishes: [
            { text: "MJF Finish",       value: "MJF_FINISH"}
        ]
    },
    {   // HP-PA12, BASF-TPU Ultrisint, PA12CB-Color, HP-PA11, PA12 FR
        material: [
            "e9a780a7-e878-46aa-8ff8-65c9991233a6", "9eec4c4e-ceb6-4ba9-83c0-405ad3207874", "HP-PA12", "BASF-TPU Ultrisint",
            "630f0469-63d1-4d40-82bd-69654ae4c494", "832a93bc-d16a-4a93-b6d9-419e7a89b021", "PA12CB-Color", "HP-PA11",
            "HP-PA12 Polar White", "PA12 FR"
        ], 
        finishes: [
            { text: "MJF Finish",       value: "MJF_FINISH"},
            { text: "Vapor Smooth",     value: "VAPOR_SMOOTHING"},
            { text: "Black Dye",        value: "BLACK_DYE"},
            { text: "Vapor Smooth / Black Dye",  value: "AMT_BLACK_DYE"}
        ]
    },
    {   // Finishing Only
        material: ["Finishing Only"],
        finishes: [
            { text: "Vapor Smooth",     value: "VAPOR_SMOOTHING"},
            { text: "Black Dye",        value: "BLACK_DYE"},
            { text: "Vapor Smooth / Black Dye",  value: "AMT_BLACK_DYE"},
            { text: "Depowder Only",    value: "DEPOWDER" }
        ]
    },
    {   // 174 PH SS
        material: ["174PH SS"],
        finishes: [
            { text: "Sinter",           value: "SINTER"}
        ]
    },
    {   // ESD PA12
        material: ["ESD PA12"],
        finishes: [
            { text: "Vapor Smooth",     value: "VAPOR_SMOOTHING"},
            { text: "Vapor Smooth / Black Dye",  value: "AMT_BLACK_DYE"}
        ]
    }
];

// 
const readable_finishes = [
    { text: "MJF Finish",       value: "MJF_FINISH"},
    { text: "Vapor Smooth",     value: "VAPOR_SMOOTHING"},
    { text: "Black Dye",        value: "BLACK_DYE"},
    { text: "Vapor Smooth / Black Dye",  value: "AMT_BLACK_DYE"},
    { text: "Depowder Only",    value: "DEPOWDER" },
    { text: "Sinter",           value: "SINTER"}
]


const Part = (props) => {

    const { config } = useContext(AppContext);


    useEffect(()=>{
        // If the snapshot_filename property isn't set, then
        // the part has not yet been fully processed on the
        // server side of the system:
        if(!props.part.snapshot_filename) {
            // So we start polling the server until the part
            // has been fully rendered:
            poll_for_rendered_part(props.part.internal_filename);
        }
    // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (props.part.snapshot_filename && !props.special_pricing) {
            console.log("--Pricing toggled to use Volume--");
            props.part.price_per_component = compute_component_price();
            props.part.price_total = compute_total_price();
            props.on_changed({ id: props.part.id, data: {...props.part}})
        }
    // eslint-disable-next-line
    }, [props.special_pricing])

    useEffect(() => {
        if (props.part.snapshot_filename) {
            change_units(props.display_unit)
        }
    // eslint-disable-next-line
    }, [props.display_unit])

    function poll_for_rendered_part(internal_filename) {
        const action = async () => {
            try {
                const result = await axios.get(config.api.base + '/parts/' + internal_filename)
                if(result.status === 204) {
                    setTimeout(() => poll_for_rendered_part(internal_filename), 1000);
                } else if (result.status === 200) {
                    // abcorp-3ds-gen will return volume and bounds in mm 
                    props.part.units = props.display_unit;
                    props.part.quantity = 1;
                    props.part.material = "HP Polypro";
                    props.part.finish = "MJF_FINISH";
                    props.part.volume = props.display_unit === "UNITS_IN" ? 
                                        Number(result.data.model_volume) / 16387.064 : 
                                        Number(result.data.model_volume);
                    props.part.price_per_component = compute_component_price();
                    props.part.price_total = compute_total_price();
                    props.part.comments = "";
                    props.part.bounds = props.display_unit === "UNITS_IN" ? 
                                        calc_bounds(result.data.model_bounds, false) : 
                                        result.data.model_bounds;
                    props.part.snapshot_filename = result.data.model_snapshot;
                    props.on_changed({ id: props.part.id, data: {...props.part}})
                }
            } catch(x) {
                console.log(x);
            }
        }
        action();
    }

    // Determines the price of the part, based on the
    // selected options (material, finish, and so on):
    function compute_component_price() {
        if (props.special_pricing && props.part.price_per_component !== undefined) {
            return props.part.price_per_component;
        }

        // Find the selected material's id in the price
        // table and use that table for recalculating:
        let relevantOffsets;
        for(let x=0;x!==props.pricing.length;x++) {
            if (props.pricing[x].material === props.part.material) {
                relevantOffsets = props.pricing[x].offsets;
                break;
            }
        }

        let normalized_volume = props.part.volume;
        // if currently displaying mm, we convert to calculate price by inches -- 9/16/2024 ez
        if (props.part.units === "UNITS_MM") normalized_volume = normalized_volume / 16387.064;

        let total_volume = props.part.quantity * normalized_volume;

        // REC: Removing the roundup of volume, per Neil's request:
        //if(normalized_volume < 1 ) {
        //    total_volume = props.part.quantity;
        //}

        console.log("::::: TOTAL VOLUME CALCULATED :::::");
        console.log("Total volume is = " + total_volume);

        // Special case: Finishing Only material is priced by finish intead of by volume
        // In case both finish and volume are required, research godel numbering -- 9/16/2024 ez
        if (props.part.material === "Finishing Only") {
            let finishNum = 1;
            switch (props.part.finish) {
                case "VAPOR_SMOOTHING":
                    finishNum = 5;
                    break;
                case "BLACK_DYE":
                    finishNum = 3;
                    break;
                case "AMT_BLACK_DYE":
                    finishNum = 7;
                    break;
                case "DEPOWDER":
                    finishNum = 11;
                    break;
                default:
            }

            let grayPartsCost = 0;
            for(let index = 0; index !== relevantOffsets.length; index++) {
                if(finishNum === relevantOffsets[index].max) {
                    grayPartsCost = total_volume * relevantOffsets[index].gray;
                    break;
                }
            }
            
            let perPartPrice = grayPartsCost / props.part.quantity;
            console.log("::::: PER PART PRICE :::::")

            perPartPrice = +(Math.round(perPartPrice + "e+2")  + "e-2");
            console.log("Rounded to: $" + perPartPrice);

            return perPartPrice;
        }

        let grayPartsCost = 0;
        //let priceByVolume = 0;

        for(let index=0;index!==relevantOffsets.length;index++) {
            if(total_volume >= relevantOffsets[index].max) {
                grayPartsCost = total_volume * relevantOffsets[index].gray;
                break;
            }
        }

        let perPartPrice = grayPartsCost / props.part.quantity;
        if((props.part.material !== "Finishing Only")) {
            switch(props.part.finish) {
                case "VAPOR_SMOOTHING":
                    perPartPrice = perPartPrice + (perPartPrice * .25);
                    break;
                case "BLACK_DYE":
                    perPartPrice = perPartPrice + (perPartPrice * .15);
                    break;
                case "AMT_BLACK_DYE" :
                    perPartPrice = perPartPrice + (perPartPrice * .30);
                    break;
                default :
                    // no price increase
            }
        }

        console.log("::::: PER PART PRICE :::::")

        perPartPrice = +(Math.round(perPartPrice + "e+2")  + "e-2");
        console.log("Rounded to: $" + perPartPrice);

        return perPartPrice;

    }

    function compute_total_price() {
        let totalPrice = props.part.price_per_component * props.part.quantity;
        return totalPrice;
    }

    function change_units(unit) {
        switch(props.part.units) {
            case "UNITS_IN" :
                if(unit === "UNITS_MM") {
                    console.log("SWITCHING FROM IN TO MM");
                    props.part.volume = props.part.volume * 16387.064;
                    props.part.bounds = calc_bounds(props.part.bounds, true);
                }
                break;
            case "UNITS_MM" :
                if(unit === "UNITS_IN") {
                    console.log("SWITCHING FROM MM TO IN");
                    props.part.volume = props.part.volume / 16387.064;
                    props.part.bounds = calc_bounds(props.part.bounds, false);
                }
                break;
            default :
        }

        props.part.units = unit;
        props.on_changed({ id: props.part.id, data: {...props.part}})
    }

    function calc_bounds(bounds, is_multiply) {
        if (is_multiply) {
            return {
                "x": bounds.x * 25.4,
                "y": bounds.y * 25.4,
                "z": bounds.z * 25.4
            }
        } else {
            return {
                "x": bounds.x / 25.4,
                "y": bounds.y / 25.4,
                "z": bounds.z / 25.4
            }
        }
    }

    function on_change_material(args) {
        props.part.material = args.target.value;
        props.part.finish = finishes_by_material.filter( 
                                finish_object => finish_object.material.includes(props.part.material)
                            )[0].finishes[0].value;
        props.part.price_per_component = compute_component_price();
        props.part.price_total = compute_total_price();
        props.on_changed({ id: props.part.id, data: { ...props.part}})
    }

    function on_change_finish(args) {
        props.part.finish = args.target.value;
        props.part.price_per_component = compute_component_price();
        props.part.price_total = compute_total_price();
        props.on_changed({id: props.part.id, data: {...props.part}})
    }

    function on_change_quantity(args) {
        if (args.target.validity.stepMismatch) args.target.value = Math.floor(args.target.value);
        props.part.quantity = args.target.validity.rangeUnderflow ? "1" : args.target.value;
        props.part.price_per_component = compute_component_price();
        props.part.price_total = compute_total_price();
        props.on_changed({ id: props.part.id, data: { ...props.part}})
    }

    function on_change_part_price(args) {
        if (props.special_pricing) {
            let inputPrice = (args.target.value <= 0 || args.target.value === null) ? 0.00 : +args.target.value;
            if (args.target.value.match(/^\d+\.\d{3}$/)) inputPrice = +args.target.value.slice(0, -1);
            props.part.price_per_component = inputPrice;
            props.part.price_total = compute_total_price();
            props.on_changed({ id: props.part.id, data: { ...props.part}})
        }
    }

    function on_change_comments(args) {
        props.part.comments = args.target.value;
        props.on_changed({ id: props.part.id, data: { ...props.part}});
    }

    function highlight_all(args) {
        args.target.select();
    }

    let display_volume = props.part.volume;
    //if(props.part.units === "UNITS_IN") {
    //    display_volume = props.part.volume / 16387.064;
    //}

    let display_bounds = "0,0,0";
    if(props.part.bounds) {
        display_bounds = Number(props.part.bounds.x).toFixed(2);
        display_bounds += ' / ';
        display_bounds += Number(props.part.bounds.y).toFixed(2);
        display_bounds += ' / ';
        display_bounds += Number(props.part.bounds.z).toFixed(2);
    }

    
    if(!props.part.snapshot_filename) {
        return (
            <div className={"flex flex-row border border-black rounded-md mt-2 overflow-hidden"}>
                <div style={{ width: "196px", height: "196px"}}>
                    <img src={"/loading.gif"} style={{ height: "100%"}} alt="A loading gif - Many 2D planes moving and intersecting in 3D" />
                </div>
                <div className={"flex-col text-center w-full pt-8"}>
                    <div className={"text-3xl"}>{props.part.original_filename}</div>
                    <div>The server is currently processing this model.</div>
                    <div>This may take seconds or minutes, depending on the size of the model.</div>
                    <Dots size={32}/>
                </div>
            </div>
        )
    } else {
        let finishes = finishes_by_material.filter( finish_object => finish_object.material.includes(props.part.material) )[0].finishes;
        
        return (
            <div className={"flex flex-rowpt-2 rounded-sm"}>
                <div className={"flex flex-col space-y-1"} style={{ width: "202px"}}>
                    <img src={config.api.base + '/temp/' + props.part.snapshot_filename} 
                        style={{ width: "170px", height: "170px"}} className={"ml-4 mt-4"} 
                        alt={"Screenshot of part " + props.part.original_filename}
                    />
                    <div className={"pl-4 pb-2"}>
                        <p className={"text-sm whitespace-nowrap"}>
                            <span className={"font-medium"}>Model Filename</span><br/>
                            {props.part.original_filename}
                        </p>
                    </div>
                </div>
                <div className={"flex flex-col flex-grow space-y-2"}>
                <div className={"flex flex-row flex-nowrap justify-center items-center space-x-4 pt-2 pl-4"}>
                    <div>
                        <label htmlFor="material" className="block text-sm font-medium text-gray-700">
                            Material
                        </label>
                        <select id="material" name="material" value={props.part.material} onChange={on_change_material} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                            { props.materials.map((item, index) => {
                                return (
                                    <option value={item.name} key={index}>{item.name}</option>
                                )
                            })}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="finish" className="block text-sm font-medium text-gray-700">
                            Finish
                        </label>
                        <select id="finish"  name="finish" value={props.part.finish} onChange={on_change_finish} style={{minWidth: "208px"}} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                            { finishes.map((item, index) => {
                                return (
                                    <option value={item.value} key={index}>{item.text}</option>
                                )
                            })}
                        </select>
                    </div>
                    <div>
                        <label htmlFor={'volume'} className={"block text-sm font-medium text-gray-700"}>Volume</label>
                        <input id={"volume"} type={"text"} size={10} readOnly={true} value={display_volume.toFixed(2)}  className={"mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"}/>
                    </div>
                    <div>
                        <label htmlFor={'bounds'} className={"block text-sm font-medium text-gray-700 whitespace-nowrap"}>Bounding box (x/y/z)</label>
                        <input id={"bounds"} type={"text"} readOnly={true} value={display_bounds}  className={"mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"}/>
                    </div>
                    <div>
                        <label htmlFor="quantity" className={"block text-sm font-medium text-gray-700"}>
                            Quantity
                        </label>
                        <input id={"quantity"} type={"number"} value={props.part.quantity} onChange={on_change_quantity} min={1} step={1} className={"mt-1 w-24 block py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"}/>
                    </div>
                    <div>
                        <label htmlFor={'partcost'} className={"block text-sm font-medium text-gray-700 whitespace-nowrap"}>
                            Cost per part
                        </label>
                        <span className={"absolute ml-3 mt-3 pt-px sm:text-sm"}>$</span>
                        <input id={"partcost"} size={8} type={"number"} step=".01" readOnly={!props.special_pricing} onChange={on_change_part_price} onFocus={highlight_all} value={ props.part.price_per_component } style={{maxWidth: "107px"}} className={"mt-1 block w-full py-2 pl-5 pr-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"}/>
                    </div>
                    <div>
                        <label htmlFor="total price" className={"block text-sm font-medium text-gray-700"}>
                            Price
                        </label>
                        <input id={"price"} size={8} type={"text"} readOnly={true} value={"$" + props.part.price_total.toFixed(2)} className={"mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"}/>
                    </div>
                    <div className={"flex justify-end flex-grow justify-end mt-6 pt-4 pr-8 h-full color-indigo-500"}>
                        <svg xmlns="http://www.w3.org/2000/svg" onClick={props.onDelete} className="h-6 w-6 stroke-current text-gray-500 hover:text-indigo-500" fill="none" viewBox="0 0 22 22">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </div>
                </div>
                <div className={"pr-6 pl-4 pt-2 pb-2"}>
                    <label className="block">
                        <span className="text-gray-700 whitespace-nowrap">Special Instructions / Comments:  </span>
                        { props.part.material === "Finishing Only" &&
                            <span className="text-red-600 whitespace-nowrap">*Finishing Only Material: Setup fee of $150 may apply.</span> 
                        } 
                        { props.part.material === "174PH SS" &&
                            <span className="text-red-600 whitespace-nowrap">*174PH SS Material: Part evaluation required. May incur additional costs. Setup fee of $150 may apply.</span>
                        }
                        <textarea className={"form-textarea mt-1 pl-2 block w-full rounded-md shadow"} rows="3" value={props.part.comments} onChange={on_change_comments}/>
                    </label>
                </div>
                </div>
            </div>
        )
    }
}

const Editor = (props) => {
    return (<Dashview {...props}><Content/></Dashview>);
}

export default Editor;
